import { Link } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useProgressSync } from "@/hooks/useProgressSync";
import { useIsMobile } from "@/hooks/use-mobile";
import { Users, ClipboardList, PenLine, BookOpen, ShoppingBag, LayoutDashboard, Menu, School } from "lucide-react";
import SyncIndicator from "@/components/SyncIndicator";
import { useState } from "react";
import { LoginPromptModal } from "@/components/LoginPromptModal";

// Module-level nav items array - prevents re-creation on every render
const NAV_ITEMS = [
  { to: "/", icon: ClipboardList, label: "Zadania" },
  { to: "/blog", icon: PenLine, label: "Blog" },
  { to: "/spolecznosc", icon: Users, label: "Społeczność" },
  { to: "/klasa", icon: School, label: "Klasa" },
  { to: "/sklepik", icon: ShoppingBag, label: "Sklepik" },
] as const;

const navLinkClass = "px-3 lg:px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-white/10 flex items-center gap-1.5 min-h-[44px] whitespace-nowrap";
const navLinkActiveClass = "bg-primary/20 text-primary";

const Header = () => {
  const { user, isAdmin } = useAuth();
  const { avatarUrl, username } = useProfile();
  const { isSyncing, manualSync } = useProgressSync();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const getInitials = () => {
    if (username) return username.slice(0, 2).toUpperCase();
    if (user?.email) return user.email.slice(0, 2).toUpperCase();
    return "U";
  };

  return (
    <header
      data-nosnippet
      className={`fixed top-0 left-0 right-0 z-50 animate-fade-in ${isMobile ? 'px-0 py-0' : 'px-4 py-3'}`}
    >
      <div className={isMobile ? '' : 'container mx-auto'}>
        <div className={`flex items-center justify-between overflow-hidden ${isMobile ? 'px-2 py-1.5 border-b border-border/30 bg-background shadow-sm' : 'glass-card px-4 py-2 rounded-sm shadow-sm'}`}>
          {/* Mobile: Center Sklepik link */}
          {isMobile && (
            <NavLink
              to="/sklepik"
              className="absolute left-1/2 -translate-x-1/2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-white/10 flex items-center gap-1.5 min-h-[40px]"
              activeClassName={navLinkActiveClass}
            >
              <ShoppingBag className="h-4 w-4" />
              Sklepik
            </NavLink>
          )}
          {/* Mobile: Hamburger Menu */}
          {isMobile ? (
            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Otwórz menu nawigacji" className="text-muted-foreground min-h-[40px] min-w-[40px]">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 bg-background/95 backdrop-blur-md">
                <SheetTitle className="sr-only">Menu nawigacji</SheetTitle>
                <nav className="flex flex-col gap-1 mt-6">
                  {NAV_ITEMS.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setIsMenuOpen(false)}
                      className={navLinkClass}
                      activeClassName={navLinkActiveClass}
                    >
                      <item.icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  ))}
                  {isAdmin && (
                    <NavLink
                      to="/baza-wiedzy"
                      onClick={() => setIsMenuOpen(false)}
                      className={navLinkClass}
                      activeClassName={navLinkActiveClass}
                    >
                      <BookOpen className="h-4 w-4" />
                      Baza wiedzy
                    </NavLink>
                  )}
                  <NavLink
                    to="/panel"
                    onClick={() => setIsMenuOpen(false)}
                    className={navLinkClass}
                    activeClassName={navLinkActiveClass}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Panel
                  </NavLink>
                </nav>
              </SheetContent>
            </Sheet>
          ) : (
            /* Desktop: Horizontal Navigation - inline map, no sub-component */
            <nav className="flex items-center gap-1 min-w-0 overflow-hidden">
              {NAV_ITEMS.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={navLinkClass}
                  activeClassName={navLinkActiveClass}
                >
                  <item.icon className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden lg:inline">{item.label}</span>
                </NavLink>
              ))}
              {isAdmin && (
                <NavLink
                  to="/baza-wiedzy"
                  className={navLinkClass}
                  activeClassName={navLinkActiveClass}
                >
                  <BookOpen className="h-4 w-4 flex-shrink-0" />
                  <span className="hidden lg:inline">Baza wiedzy</span>
                </NavLink>
              )}
            </nav>
          )}

          {/* Right side - Sync indicator, User Panel & Auth buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* SyncIndicator hidden – sync still works in background */}
            {!isMobile && (
              <NavLink
                to="/panel"
                className="px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 text-muted-foreground hover:text-foreground hover:bg-white/10 flex items-center gap-1.5"
                activeClassName={navLinkActiveClass}
              >
                <LayoutDashboard className="h-4 w-4" />
                Panel
              </NavLink>
            )}
            {user ? (
              <Link to="/panel" className="flex items-center gap-2 group">
                <Avatar className="h-8 w-8 border-2 border-transparent group-hover:border-primary/50 transition-all duration-200">
                  {avatarUrl ? (
                    <AvatarImage src={avatarUrl} alt={username || "Avatar"} />
                  ) : null}
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-medium">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
              </Link>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  className={`text-muted-foreground hover:text-foreground hover:bg-white/10 ${isMobile ? 'min-h-[40px]' : 'min-h-[44px]'}`}
                  onClick={() => setShowLoginModal(true)}
                >
                  {isMobile ? "Zaloguj" : "Zaloguj się"}
                </Button>
                <LoginPromptModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
